import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './LanguageDetectionDialog.css';

/**
 * Diálogo modal que aparece ANTES de reproducir el video
 * - Muestra idioma detectado vs idioma de sesión
 * - Opciones: Ver Original o Traducir con IA
 * - Maneja la traducción si es seleccionada
 */
export function LanguageDetectionDialog({
  isOpen,
  detectedLanguage,
  detectedConfidence,
  sessionLanguage,
  videoTitle,
  resourceId,
  onPlayOriginal,
  onPlayTranslated,
  onCancel,
}) {
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState(0);
  const [translationError, setTranslationError] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

  console.log('📊 [LanguageDetectionDialog] Props received:', {
    detectedLanguage,
    sessionLanguage,
    videoTitle,
    resourceId,
  });

  // Mapeo de códigos de idioma a nombres legibles
  const languageNames = {
    'en': '🇺🇸 English',
    'es': '🇪🇸 Español',
    'fr': '🇫🇷 Français',
    'de': '🇩🇪 Deutsch',
    'it': '🇮🇹 Italiano',
    'pt': '🇵🇹 Português',
    'zh': '🇨🇳 中文',
    'ja': '🇯🇵 日本語',
    'ko': '🇰🇷 한국어',
    'ru': '🇷🇺 Русский',
    'ar': '🇸🇦 العربية',
    'hi': '🇮🇳 हिन्दी',
  };

  const detectedLangName = languageNames[detectedLanguage] || detectedLanguage.toUpperCase();
  const sessionLangName = languageNames[sessionLanguage] || sessionLanguage.toUpperCase();

  const handlePlayOriginal = () => {
    console.log('📹 [LanguageDetectionDialog] User chose: Play Original');
    onPlayOriginal();
  };

  const handleTranslateWithAI = async () => {
    setIsTranslating(true);
    setTranslationError(null);
    setTranslationProgress(0);

    try {
      console.log('🎬 [LanguageDetectionDialog] Starting translation to:', sessionLanguage);

      // POST to translate endpoint
      const response = await axios.post(
        `${API_BASE}/resources/translate`,
        {
          resourceId,
          targetLanguage: sessionLanguage,
        },
        { timeout: 600000 } // 10 minutes timeout
      );

      const { taskId } = response.data;
      console.log('📋 [Translation] Task created:', taskId);

      // Polling para monitorear progreso de traducción
      let translationData = null;
      let attempts = 0;
      const maxAttempts = 600; // 10 minutos máximo

      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 1000)); // Esperar 1 segundo

        const statusResponse = await axios.get(`${API_BASE}/tasks/${taskId}`);
        translationData = statusResponse.data;

        // Calcular progreso basado en intentos
        setTranslationProgress(Math.min((attempts / maxAttempts) * 100, 95));

        if (translationData.status === 'completed') {
          console.log('✅ [Translation] Completed!');
          setTranslationProgress(100);

          // Build URL to the integrated VTS player
          const backendHost = API_BASE.replace(/\/api$/, '');
          const playerUrl = `${backendHost}/api/vts/player/${taskId}`;
          console.log('📹 [Translation] Opening player at:', playerUrl);

          // Esperar 1 segundo antes de pasar al player
          await new Promise(resolve => setTimeout(resolve, 1000));
          onPlayTranslated(playerUrl);
          return;
        } else if (translationData.status === 'failed') {
          setTranslationError(translationData.error || 'Traducción falló');
          console.error('❌ [Translation] Failed:', translationData.error);
          setIsTranslating(false);
          return;
        }

        attempts++;
      }

      setTranslationError('Tiempo de espera agotado para traducción');
      console.warn('⚠️ [Translation] Timeout reached');
      setIsTranslating(false);
    } catch (error) {
      console.error('❌ [Translation] Error:', error);
      setTranslationError(error.response?.data?.msg || 'Error al traducir video');
      setIsTranslating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="ldd-overlay">
      <div className="ldd-dialog">
        {/* Fase: Mostrando opciones */}
        {!isTranslating && (
          <>
            <div className="ldd-header">
              <h3>🎥 Detección de Idioma</h3>
              <p className="ldd-subtitle">{videoTitle}</p>
            </div>

            <div className="ldd-content">
              <div className="ldd-detected-box">
                <div className="ldd-detected-item">
                  <span className="ldd-label">Video detectado:</span>
                  <span className="ldd-value">{detectedLangName}</span>
                  <span className="ldd-confidence">
                    Confianza: {(detectedConfidence * 100).toFixed(0)}%
                  </span>
                </div>

                <div className="ldd-arrow">→</div>

                <div className="ldd-session-item">
                  <span className="ldd-label">Tu sesión:</span>
                  <span className="ldd-value">{sessionLangName}</span>
                </div>
              </div>

              <div className="ldd-message">
                <p>🤖 ¿Deseas que traduzca el audio a tu idioma?</p>
              </div>
            </div>

            <div className="ldd-actions">
              <button
                className="ldd-btn ldd-btn-primary"
                onClick={handlePlayOriginal}
              >
                👁️ Ver Original
              </button>
              <button
                className="ldd-btn ldd-btn-secondary"
                onClick={handleTranslateWithAI}
              >
                🎬 Traducir con IA
              </button>
              <button
                className="ldd-btn ldd-btn-cancel"
                onClick={onCancel}
              >
                ✕ Cancelar
              </button>
            </div>
          </>
        )}

        {/* Fase: Traduciendo */}
        {isTranslating && !translationError && (
          <>
            <div className="ldd-header">
              <h3>⏳ Traduciendo Video</h3>
            </div>

            <div className="ldd-translating">
              <div className="ldd-spinner"></div>
              <p>Traduciendo audio a {sessionLangName}...</p>

              <div className="ldd-progress-container">
                <div
                  className="ldd-progress-bar"
                  style={{ width: `${translationProgress}%` }}
                ></div>
              </div>
              <p className="ldd-progress-text">{Math.round(translationProgress)}%</p>
            </div>
          </>
        )}

        {/* Fase: Error */}
        {translationError && (
          <>
            <div className="ldd-header">
              <h3>❌ Error en la Traducción</h3>
            </div>

            <div className="ldd-error">
              <p>{translationError}</p>
            </div>

            <div className="ldd-actions">
              <button
                className="ldd-btn ldd-btn-primary"
                onClick={handlePlayOriginal}
              >
                Ver Original
              </button>
              <button
                className="ldd-btn ldd-btn-cancel"
                onClick={onCancel}
              >
                Cancelar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LanguageDetectionDialog;
